import React, { useState } from "react";
import axios from "axios";
import { Flag, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const REASONS = [
  { value: "misleading", label: "Misleading" },
  { value: "offensive", label: "Offensive" },
  { value: "not_a_real_business", label: "Not a real business" },
  { value: "wrong_information", label: "Wrong information" },
  { value: "broken_link", label: "Broken link" },
  { value: "other", label: "Other" },
];

export default function ReportListingLink({ zipCode, listingType, listingId }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("misleading");
  const [details, setDetails] = useState("");
  const [reporterEmail, setReporterEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const openModal = () => {
    setReason("misleading");
    setDetails("");
    setReporterEmail("");
    setMessage("");
    setError("");
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(`${API}/reports`, {
        zip_code: zipCode,
        listing_type: listingType,
        listing_id: listingId,
        reason,
        details,
        reporter_email: reporterEmail,
      });
      setMessage(res.data.message || "Thank you — we'll review this listing.");
      setTimeout(() => setOpen(false), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not submit report");
    }
    setSubmitting(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-[#64748B] hover:underline transition-colors"
        data-testid="report-listing-link"
      >
        <Flag size={11} /> Report this listing
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(15,23,42,0.5)" }}
          onClick={() => setOpen(false)}
          data-testid="report-listing-modal"
        >
          <div
            className="bg-white border border-slate-200 rounded-xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#0F172A] text-sm">Report this listing</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-slate-400 hover:text-[#0F172A] transition-colors"
                data-testid="report-listing-close"
              >
                <X size={18} />
              </button>
            </div>

            {message ? (
              <p className="text-sm text-[#27AE60] font-semibold py-4" data-testid="report-listing-success">
                {message}
              </p>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="report-listing-form">
                <div>
                  <label className="text-xs font-semibold text-[#64748B] block mb-1">Reason</label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="input-field !text-sm"
                    data-testid="report-reason-select"
                  >
                    {REASONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#64748B] block mb-1">Details (optional)</label>
                  <textarea
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    maxLength={1000}
                    rows={3}
                    placeholder="Tell us what's wrong..."
                    className="input-field !py-2.5 !text-sm resize-none"
                    data-testid="report-details-textarea"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#64748B] block mb-1">Your email (optional)</label>
                  <input
                    type="email"
                    value={reporterEmail}
                    onChange={(e) => setReporterEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="input-field !py-2.5 !text-sm"
                    data-testid="report-email-input"
                  />
                </div>
                {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-action w-full !py-2.5 !text-sm disabled:opacity-60"
                  data-testid="report-submit-btn"
                >
                  {submitting ? "Submitting..." : "Submit Report"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
